/**
 * Wiederholen (Story 23.11, Entwurf A „Wiederholen als Eigenschaft im Blatt“): Zeile „Wiederholen“ im Detail-Blatt mit
 * Auswahl-Menü (am Handy zweites Blatt von unten, über `Popover`). Vorschläge aus dem Fälligkeitstag, „Eigene Regel …“
 * klappt die übrigen Auswahlen des Kerns auf; darunter der Satz „…, nächste: Mo 01.03.2027“ und die nächsten 5 Termine
 * vom Server (`GET /tasks/recurrence-preview`, nie im Web gerechnet) – verschobene bernstein mit Grund als Tooltip und
 * für Bildschirmleser. Speichern sofort beim Klick, danach 10 s „Rückgängig“. ↻ an Zeile und Karte: `RecurrenceMark`.
 */
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Repeat } from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  recurrenceDayLabel,
  recurrenceSuggestions,
  recurrenceWeekdayShort,
  samePattern,
  isoWeekdayOf,
  TASK_RECURRENCE_TEXTS,
  type RecurrencePatternInput,
  type RecurrenceRuleBody,
} from '@till-erp/collab/values';
import { apiFetch, ApiProblem, newIdempotencyKey, type SettingView, type TaskRecurrence, type TaskRecurrencePreview } from '../lib/api.ts';
import { formatDate } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import type { UndoOffer } from './undo-toast.tsx';
import { DateField } from './ui/date-field.tsx';
import { Input } from './ui/input.tsx';
import { Popover } from './ui/popover.tsx';
import { radioKeyTarget } from './ui/radio-keys.ts';
import { Segment } from './ui/segment.tsx';
import { pillToneClass } from './ui/pill-tone.ts';

const T = TASK_RECURRENCE_TEXTS;

/** ↻ an Zeile und Karte: Tooltip und Bildschirmleser „Wiederholt sich: jeden Montag“ (kein zweites Wort daneben). */
export function RecurrenceMark({ recurrence, className }: { recurrence: Pick<TaskRecurrence, 'text' | 'status'> | null | undefined; className?: string }) {
  if (!recurrence) return null;
  const label = recurrence.status === 'paused' ? `${T.marker(recurrence.text)} (${T.paused})` : T.marker(recurrence.text);
  return (
    <span role="img" aria-label={label} title={label} data-testid="wiederholt-zeichen" className={cn('inline-flex flex-none items-center text-ink-3', recurrence.status === 'paused' && 'opacity-60', className)}>
      <Repeat size={13} strokeWidth={2.25} aria-hidden />
    </span>
  );
}

type Unit = 'days' | 'weeks' | 'months' | 'years';
type MonthKind = 'day' | 'workday';

function unitOf(pattern: RecurrencePatternInput): Unit {
  switch (pattern.kind) {
    case 'daily':
    case 'workdays':
      return 'days';
    case 'weekly':
      return 'weeks';
    case 'monthlyDay':
    case 'monthlyWorkday':
      return 'months';
    case 'yearly':
      return 'years';
  }
}

/** Muster einer Einheit aus dem Fälligkeitstag (Wechsel der Einheit in „Eigene Regel …“). */
function patternForUnit(unit: Unit, dueDate: string, previous: RecurrencePatternInput): RecurrencePatternInput {
  const [, m, d] = dueDate.split('-').map(Number) as [number, number, number];
  switch (unit) {
    case 'days':
      return { kind: 'daily' };
    case 'weeks':
      return previous.kind === 'weekly' ? previous : { kind: 'weekly', weekdays: [isoWeekdayOf(dueDate)], intervalWeeks: 1 };
    case 'months':
      return { kind: 'monthlyDay', day: d };
    case 'years':
      return { kind: 'yearly', month: m, day: d };
  }
}

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? (Object.values(err.errors)[0] ?? err.detail) : 'Speichern ging nicht. Bitte noch einmal versuchen.';
}

/** Fehler an Feldern der eigenen Regel (`pattern.weekdays`, `end.count` …); andere stehen als Satz unter dem Menü. */
function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiProblem)) return {};
  return Object.fromEntries(Object.entries(err.errors).filter(([k]) => k.startsWith('pattern.') || k.startsWith('end.')));
}

/**
 * Zeile „Wiederholen“ im Detail-Blatt (nur freie Aufgaben). `recurrence` = laufende oder pausierte Wiederholung der
 * Aufgabe; `onSaved` lädt Blatt und Liste neu.
 */
export function RecurrenceChooser({
  taskId,
  dueDate,
  recurrence,
  disabled,
  onSaved,
  offerUndo,
}: {
  taskId: string;
  dueDate: string;
  recurrence: TaskRecurrence | null;
  disabled?: boolean;
  onSaved: () => Promise<unknown>;
  offerUndo: (o: UndoOffer) => void;
}) {
  const { getToken } = useSession();
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Entwurf der eigenen Regel, solange er (noch) nicht gespeichert ist – z. B. ohne Wochentag. */
  const [draft, setDraft] = useState<RecurrenceRuleBody | null>(null);
  const ids = { interval: useId(), count: useId(), until: useId(), list: useId() };
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const current: RecurrenceRuleBody | null = recurrence ? { pattern: recurrence.rule.pattern, nonWorkday: recurrence.rule.nonWorkday, end: recurrence.rule.end } : null;
  const shown = draft ?? current;
  const suggestions = recurrenceSuggestions(dueDate);

  const send = <R,>(method: 'POST' | 'PATCH', path: string, body: unknown) => apiFetch<R>(getToken, path, { method, body, idempotencyKey: newIdempotencyKey() });

  /** Regel speichern: neu (POST), ändern (PATCH) oder beenden (`null`); danach „Rückgängig“ zum vorigen Stand. */
  const save = async (next: RecurrenceRuleBody | null) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      if (next === null) {
        // Ohne Wiederholung: nur den Entwurf verwerfen (auch einen ungültigen).
        if (!recurrence) {
          setDraft(null);
          return;
        }
        const ended = await send<TaskRecurrence>('POST', `/api/v1/tasks/recurrences/${recurrence.id}/end`, { version: recurrence.version, taskId });
        setDraft(null);
        offerUndo({
          text: T.removedNotice,
          undo: async () => {
            await send('POST', `/api/v1/tasks/recurrences/${ended.id}/end`, { version: ended.version, ended: false, taskId });
            await onSaved();
          },
        });
      } else if (!recurrence) {
        const made = await send<TaskRecurrence>('POST', `/api/v1/tasks/${taskId}/recurrence`, next);
        setDraft(null);
        offerUndo({
          text: T.savedNotice(made.text),
          undo: async () => {
            await send('POST', `/api/v1/tasks/recurrences/${made.id}/end`, { version: made.version, taskId });
            await onSaved();
          },
        });
      } else {
        // Rückgängig stellt die vorige Reihe samt Start wieder her (sonst zählte „n-mal“ ab dieser Aufgabe neu).
        const before = { ...current!, start: recurrence.rule.start, countOffset: recurrence.countOffset };
        const changed = await send<TaskRecurrence>('PATCH', `/api/v1/tasks/recurrences/${recurrence.id}`, { ...next, version: recurrence.version, taskId });
        setDraft(null);
        offerUndo({
          text: T.savedNotice(changed.text),
          undo: async () => {
            await send('PATCH', `/api/v1/tasks/recurrences/${changed.id}`, { ...before, version: changed.version, taskId });
            await onSaved();
          },
        });
      }
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      // Feldfehler stehen am Feld; der Entwurf bleibt sichtbar (z. B. kein Wochentag gewählt).
      if (next !== null && Object.keys(fields).length > 0) {
        setDraft(next);
        setCustomOpen(true);
      } else setError(problemText(err));
    } finally {
      setBusy(false);
      await onSaved();
    }
  };

  const base: Omit<RecurrenceRuleBody, 'pattern'> = { nonWorkday: shown?.nonWorkday ?? 'previous', end: shown?.end ?? { kind: 'never' } };
  const choices: { key: string; label: string; hint?: string; selected: boolean; rule: RecurrenceRuleBody | null }[] = [
    { key: 'none', label: T.none, selected: shown === null, rule: null },
    ...suggestions.map((s) => ({
      key: s.key,
      label: s.label,
      hint: s.key === 'workdays' ? 'Mo–Fr ohne Feiertage' : undefined,
      selected: shown !== null && samePattern(shown.pattern, s.pattern),
      rule: { ...base, pattern: s.pattern },
    })),
  ];
  const selectedIndex = choices.findIndex((c) => c.selected);

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const index = optionRefs.current.findIndex((el) => el === document.activeElement);
    const next = radioKeyTarget(e.key, index, choices.length);
    if (next === null) return;
    e.preventDefault();
    optionRefs.current[next]?.focus();
  };

  const editCustom = (patch: Partial<RecurrenceRuleBody>) => {
    const from = shown ?? { pattern: suggestions[2]!.pattern, nonWorkday: 'previous' as const, end: { kind: 'never' as const } };
    void save({ ...from, ...patch });
  };

  const label = recurrence ? recurrence.text : T.none;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setError(null);
          setErrors({});
          setDraft(null);
        }
      }}
      label={T.label}
      className="w-[min(380px,calc(100vw-32px))]"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          disabled={disabled}
          data-testid="aufgabe-wiederholen"
          className={cn(
            '-mx-space-2 inline-flex min-h-9 cursor-pointer items-center gap-space-2 rounded-field px-space-2 py-space-1 text-left text-row transition-colors hover:bg-tile aria-expanded:bg-tile disabled:cursor-default pointer-coarse:min-h-11',
            recurrence ? 'bg-tile font-medium text-brand' : 'text-ink-3',
          )}
        >
          <span className="sr-only">{`${T.label}:`}</span>
          <Repeat size={14} strokeWidth={2.25} aria-hidden />
          <span className="min-w-0 truncate">{label}</span>
          {recurrence?.status === 'paused' ? <span className={cn('rounded-full px-space-2 text-pill', pillToneClass('check'))}>{T.paused}</span> : null}
        </button>
      )}
    >
      <div className="flex flex-col gap-space-1 p-space-2" data-testid="wiederholen-menue">
        <p className="px-space-2 pb-space-1 text-caption text-ink-3">{T.menuTitle(dueDate)}</p>
        <div role="radiogroup" aria-label={T.label} id={ids.list} onKeyDown={onListKey} className="flex flex-col">
          {choices.map((c, i) => (
            <button
              key={c.key}
              ref={(el) => {
                optionRefs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={c.selected}
              tabIndex={i === (selectedIndex < 0 ? 0 : selectedIndex) ? 0 : -1}
              aria-disabled={busy || undefined}
              data-testid={`wiederholen-${c.key}`}
              onClick={() => {
                if (!c.selected) void save(c.rule);
              }}
              className={cn(
                'flex min-h-11 cursor-pointer items-center gap-space-2 rounded-field px-space-2 text-left text-row transition-colors hover:bg-tile focus-visible:bg-tile md:min-h-9',
                c.selected && 'bg-tile font-medium text-brand',
              )}
            >
              <span className="min-w-0 flex-1 truncate">{c.label}</span>
              {c.hint ? <span className="flex-none text-caption text-ink-3">{c.hint}</span> : null}
              {c.selected ? <Check size={16} strokeWidth={2.5} aria-hidden className="flex-none" /> : null}
            </button>
          ))}
        </div>

        <div className="mt-space-1 border-t border-line pt-space-1">
          <button
            type="button"
            aria-expanded={customOpen}
            data-testid="wiederholen-eigene"
            onClick={() => setCustomOpen((v) => !v)}
            className="flex min-h-11 w-full cursor-pointer items-center justify-between rounded-field px-space-2 text-left text-row font-medium hover:bg-tile md:min-h-9"
          >
            {T.custom}
            <ChevronDown size={16} aria-hidden className={cn('text-ink-3 transition-transform', customOpen && 'rotate-180')} />
          </button>
          {customOpen ? (
            <CustomRule
              rule={shown ?? { pattern: suggestions[2]!.pattern, nonWorkday: 'previous', end: { kind: 'never' } }}
              dueDate={dueDate}
              busy={busy}
              errors={errors}
              ids={ids}
              onChange={editCustom}
            />
          ) : null}
        </div>

        {error ? (
          <p role="alert" className="px-space-2 text-body text-clarify">
            {error}
          </p>
        ) : null}
        {shown && !draft ? <PreviewBox rule={shown} start={recurrence?.rule.start ?? dueDate} countOffset={recurrence?.countOffset ?? 0} dueDate={dueDate} /> : null}
      </div>
    </Popover>
  );
}

/** Grenze der Eingabe aus den Einstellungen (geprüft wird im Command); solange sie lädt, ohne `max`. */
function useLimit(key: string): number | undefined {
  const { getToken } = useSession();
  const view = useQuery({ queryKey: ['setting', key], queryFn: () => apiFetch<SettingView>(getToken, `/api/v1/settings/${key}`), staleTime: 300_000 });
  return typeof view.data?.value === 'number' ? view.data.value : undefined;
}

/** „Eigene Regel …“: Einheit, Wochentage bzw. Tag, Kein Arbeitstag, Ende. Jede Änderung speichert sofort. */
function CustomRule({
  rule,
  dueDate,
  busy,
  errors,
  ids,
  onChange,
}: {
  rule: RecurrenceRuleBody;
  dueDate: string;
  busy: boolean;
  errors: Record<string, string>;
  ids: { interval: string; count: string; until: string };
  onChange: (patch: Partial<RecurrenceRuleBody>) => void;
}) {
  const unit = unitOf(rule.pattern);
  const intervalMax = useLimit('task_recurrence_interval_weeks_max');
  const countMax = useLimit('task_recurrence_count_max');
  const [intervalDraft, setIntervalDraft] = useState<string | null>(null);
  const [countDraft, setCountDraft] = useState<string | null>(null);
  const weekdays = rule.pattern.kind === 'weekly' ? rule.pattern.weekdays : [];
  const intervalWeeks = rule.pattern.kind === 'weekly' ? rule.pattern.intervalWeeks : 1;
  const monthKind: MonthKind = rule.pattern.kind === 'monthlyWorkday' ? 'workday' : 'day';
  const dueDay = Number(dueDate.slice(8, 10));
  const weekdayError = errors['pattern.weekdays'];
  const intervalError = errors['pattern.intervalWeeks'];
  const countError = errors['end.count'];
  const untilError = errors['end.date'];

  const commitInterval = () => {
    // Während des Speicherns bleibt der Entwurf stehen (sonst ginge die Eingabe still verloren); Enter/Verlassen danach speichert.
    if (busy || intervalDraft === null || rule.pattern.kind !== 'weekly') return;
    const n = Number(intervalDraft);
    setIntervalDraft(null);
    if (String(n) === String(intervalWeeks)) return;
    onChange({ pattern: { ...rule.pattern, intervalWeeks: Number.isInteger(n) ? n : 0 } });
  };
  const commitCount = () => {
    if (busy || countDraft === null || rule.end.kind !== 'count') return;
    const n = Number(countDraft);
    setCountDraft(null);
    if (n === rule.end.count) return;
    onChange({ end: { kind: 'count', count: Number.isInteger(n) ? n : 0 } });
  };

  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-space-3 gap-y-space-3 px-space-2 pt-space-2 pb-space-1" data-testid="wiederholen-eigene-regel">
      <span className="text-caption text-ink-3">Einheit</span>
      <Segment<Unit>
        label="Einheit"
        dense
        busy={busy}
        value={unit}
        onChange={(u) => onChange({ pattern: patternForUnit(u, dueDate, rule.pattern) })}
        options={(['days', 'weeks', 'months', 'years'] as const).map((u) => ({ value: u, label: T.units[u] }))}
      />

      {unit === 'days' ? (
        <>
          <span className="text-caption text-ink-3">Tage</span>
          <Segment<'daily' | 'workdays'>
            label="Welche Tage"
            dense
            busy={busy}
            value={rule.pattern.kind === 'workdays' ? 'workdays' : 'daily'}
            onChange={(k) => onChange({ pattern: { kind: k } })}
            options={[
              { value: 'daily', label: 'jeden Tag' },
              { value: 'workdays', label: 'nur Arbeitstage' },
            ]}
          />
        </>
      ) : null}

      {unit === 'weeks' ? (
        <>
          <label htmlFor={ids.interval} className="text-caption text-ink-3">
            Alle
          </label>
          <span className="flex flex-col gap-space-1">
            <span className="flex items-center gap-space-2">
              <Input
                id={ids.interval}
                inputMode="numeric"
                className="w-16"
                aria-invalid={intervalError ? true : undefined}
                aria-describedby={intervalError ? `${ids.interval}-fehler` : undefined}
                value={intervalDraft ?? String(intervalWeeks)}
                readOnly={busy}
                onChange={(e) => setIntervalDraft(e.target.value)}
                onBlur={commitInterval}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitInterval();
                }}
                max={intervalMax}
              />
              <span className="text-body text-ink-3">{intervalWeeks === 1 ? 'Woche' : 'Wochen'}</span>
            </span>
            {intervalError ? (
              <span id={`${ids.interval}-fehler`} className="text-caption text-clarify">
                {intervalError}
              </span>
            ) : null}
          </span>
          <span className="text-caption text-ink-3">am</span>
          <span className="flex flex-col gap-space-1">
            <span role="group" aria-label="Wochentage" className="flex flex-wrap gap-space-1" data-testid="wiederholen-wochentage">
              {recurrenceWeekdayShort.map((name, i) => {
                const day = i + 1;
                const on = weekdays.includes(day);
                return (
                  <button
                    key={name}
                    type="button"
                    aria-pressed={on}
                    aria-disabled={busy || undefined}
                    aria-describedby={weekdayError ? 'wiederholen-wochentage-fehler' : undefined}
                    onClick={() => {
                      if (busy || rule.pattern.kind !== 'weekly') return;
                      const next = on ? weekdays.filter((w) => w !== day) : [...weekdays, day].sort((a, b) => a - b);
                      onChange({ pattern: { ...rule.pattern, weekdays: next } });
                    }}
                    className={cn(
                      'grid size-9 cursor-pointer place-items-center rounded-full text-caption font-medium transition-colors pointer-coarse:size-11',
                      on ? 'bg-brand text-on-brand' : 'bg-tile text-ink-2 hover:bg-chip',
                    )}
                  >
                    {name}
                  </button>
                );
              })}
            </span>
            {weekdayError ? (
              <span id="wiederholen-wochentage-fehler" role="alert" className="text-caption text-clarify">
                {weekdayError}
              </span>
            ) : null}
          </span>
        </>
      ) : null}

      {unit === 'months' ? (
        <>
          <span className="text-caption text-ink-3">am</span>
          <span className="flex flex-col gap-space-2">
            <Segment<MonthKind>
              label="Tag im Monat"
              dense
              busy={busy}
              value={monthKind}
              onChange={(k) => onChange({ pattern: k === 'day' ? { kind: 'monthlyDay', day: dueDay } : { kind: 'monthlyWorkday', nth: 'last' } })}
              options={[
                { value: 'day', label: `${dueDay}.` },
                { value: 'workday', label: 'Arbeitstag' },
              ]}
            />
            {rule.pattern.kind === 'monthlyWorkday' ? (
              <Segment<string>
                label="Welcher Arbeitstag"
                dense
                busy={busy}
                value={String(rule.pattern.nth)}
                onChange={(v) => onChange({ pattern: { kind: 'monthlyWorkday', nth: v === 'last' ? 'last' : Number(v) } })}
                options={[
                  { value: '1', label: '1.' },
                  { value: '2', label: '2.' },
                  { value: '3', label: '3.' },
                  { value: 'last', label: 'letzter' },
                ]}
              />
            ) : null}
          </span>
        </>
      ) : null}

      {unit === 'years' && rule.pattern.kind === 'yearly' ? (
        <>
          <span className="text-caption text-ink-3">am</span>
          <span className="text-row">{`${String(rule.pattern.day).padStart(2, '0')}.${String(rule.pattern.month).padStart(2, '0')}. (aus Fällig)`}</span>
        </>
      ) : null}

      <span className="text-caption leading-tight text-ink-3">
        Kein
        <br />
        Arbeitstag
      </span>
      <Segment<RecurrenceRuleBody['nonWorkday']>
        label="Fällt auf keinen Arbeitstag"
        dense
        busy={busy}
        value={rule.nonWorkday}
        onChange={(nonWorkday) => onChange({ nonWorkday })}
        options={(['previous', 'next', 'keep'] as const).map((v) => ({ value: v, label: T.nonWorkday[v] }))}
      />

      <span className="text-caption text-ink-3">Ende</span>
      <span className="flex flex-col gap-space-2">
        <Segment<RecurrenceRuleBody['end']['kind']>
          label="Ende"
          dense
          busy={busy}
          value={rule.end.kind}
          onChange={(kind) => onChange({ end: kind === 'never' ? { kind } : kind === 'until' ? { kind, date: addMonths(dueDate, 3) } : { kind, count: 10 } })}
          options={(['never', 'until', 'count'] as const).map((v) => ({ value: v, label: T.endKinds[v] }))}
        />
        {rule.end.kind === 'until' ? (
          <span className="flex flex-col gap-space-1">
            <DateField
              id={ids.until}
              value={rule.end.date}
              today={dueDate}
              min={dueDate}
              invalid={Boolean(untilError)}
              aria-label="Enddatum"
              onChange={(date) => {
                if (date && rule.end.kind === 'until' && date !== rule.end.date) onChange({ end: { kind: 'until', date } });
              }}
            />
            {untilError ? <span className="text-caption text-clarify">{untilError}</span> : null}
          </span>
        ) : null}
        {rule.end.kind === 'count' ? (
          <span className="flex flex-col gap-space-1">
            <span className="flex items-center gap-space-2">
              <Input
                id={ids.count}
                inputMode="numeric"
                className="w-20"
                aria-label="Anzahl Termine"
                aria-invalid={countError ? true : undefined}
                value={countDraft ?? String(rule.end.count)}
                readOnly={busy}
                onChange={(e) => setCountDraft(e.target.value)}
                onBlur={commitCount}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitCount();
                }}
                max={countMax}
              />
              <span className="text-body text-ink-3">-mal</span>
            </span>
            {countError ? <span className="text-caption text-clarify">{countError}</span> : null}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/** Kalendertage-Rechnung nur für den Startwert des Enddatums (Vorschlag im Feld; die Termine rechnet der Server). */
function addMonths(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const next = new Date(Date.UTC(y, m - 1 + months, Math.min(d, 28)));
  return next.toISOString().slice(0, 10);
}

/** Satz + 5 Termine (Server-Vorschau): erste dunkelblau, verschobene bernstein mit Grund; darunter Gründe und Vorlauf. */
function PreviewBox({ rule, start, countOffset, dueDate }: { rule: RecurrenceRuleBody; start: string; countOffset: number; dueDate: string }) {
  const { getToken } = useSession();
  // Gespeicherte Regel mit ihrem Start und Restzahl (sonst zählte „n-mal“ ab dieser Aufgabe neu).
  const full = { start, ...rule, ...(rule.end.kind === 'count' ? { end: { kind: 'count' as const, count: Math.max(1, rule.end.count - countOffset) } } : {}) };
  const key = JSON.stringify(full);
  const preview = useQuery({
    queryKey: ['tasks', 'recurrence-preview', key],
    queryFn: () => apiFetch<TaskRecurrencePreview>(getToken, `/api/v1/tasks/recurrence-preview?rule=${encodeURIComponent(key)}&after=${dueDate}`),
    staleTime: 60_000,
  });
  const data = preview.data;
  if (!data) return <div className="mx-space-2 mt-space-2 h-24 animate-pulse rounded-card bg-tile" aria-hidden />;
  const [head, next] = data.sentence.split(', nächste: ');
  const shifted = data.next.filter((n) => n.reason !== null);
  return (
    <div className="mx-space-2 mt-space-2 flex flex-col gap-space-2 rounded-card bg-tile p-space-3" data-testid="wiederholen-vorschau">
      <p className="text-row font-medium" data-testid="wiederholen-satz">
        {next ? (
          <>
            {head}, <span className="text-brand">nächste: {next}</span>
          </>
        ) : (
          data.sentence
        )}
      </p>
      <ol className="grid grid-cols-5 gap-space-1" aria-label="Nächste Termine">
        {data.next.map((n, i) => {
          const label = n.reason ? `${recurrenceDayLabel(n.date)}, verschoben: ${n.reason}` : recurrenceDayLabel(n.date);
          return (
            <li key={n.index} className="flex flex-col items-center gap-space-1" title={n.reason ?? undefined} aria-label={label} data-testid={n.shifted ? 'termin-verschoben' : 'termin'}>
              <span aria-hidden className={cn('h-1.5 w-full rounded-full', n.shifted ? 'bg-check' : i === 0 ? 'bg-brand' : 'bg-line')} />
              <span aria-hidden className={cn('text-caption tabular-nums', n.shifted ? 'font-medium text-check' : i === 0 ? 'font-semibold text-brand' : 'text-ink-3')}>
                {formatDate(n.date).slice(0, 6)}
                {n.shifted ? (n.date < n.scheduledDate ? ' ←' : ' →') : ''}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="text-caption text-ink-3">
        {shifted.map((n) => (
          <span key={n.index} className="block">
            {n.reason}
          </span>
        ))}
        <span className="block">{data.leadText}</span>
      </p>
    </div>
  );
}
